import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IUserQuery } from 'app/projects/user/src/lib/interfaces/user.query.interface';
import { User } from 'app/projects/user/src/lib/models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IChoosenFilterInterface } from '../interfaces/choosen-filter.interface';
import { ExtendedAttributeFilterInterface } from '../interfaces/extended-attribute-filer.interface';

export interface IUsersAndTotalCount {
    data: IUserQuery[];
    user_ids?: number[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}
export interface IRootAudienceUsersAndTotalCount {
    data: { sub_audience: []; user: IUserQuery }[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

/**
 * I had to use a custom encoding
 * because we use the bracket in the url for the FILTER user
 */
export class MyCustomHttpUrlEncodingCodec extends HttpUrlEncodingCodec {
    encodeKey(k: string): string {
        return super.encodeKey(k).replace(new RegExp('%5B', 'g'), '[').replace(new RegExp('%5D', 'g'), ']');
    }
}

@Injectable()
export class AllUsersApiService {
    private _filtersList = new BehaviorSubject<IChoosenFilterInterface[]>([]);
    public filtersList$ = this._filtersList.asObservable();

    private _growFiltersList = new BehaviorSubject<IChoosenFilterInterface[]>([]);
    public growFiltersList$ = this._growFiltersList.asObservable();

    private _selectedAudience = new BehaviorSubject<MaxBrainAudience>(null);
    public selectedAudience$ = this._selectedAudience.asObservable();

    public isSingleSelect: boolean = false;

    constructor(private http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {}

    private _getValueFromSessionStorage<T>(key: string, fallback: T): T {
        const rawValue = sessionStorage.getItem(key);

        if (!rawValue || rawValue === 'undefined') {
            return fallback;
        }

        try {
            const parsedValue = JSON.parse(rawValue) as T;
            return parsedValue ?? fallback;
        } catch {
            return fallback;
        }
    }

    setFiltersFromStorage(key: string): void {
        const filters = this._getValueFromSessionStorage<IChoosenFilterInterface[]>(key, []);
        this._filtersList.next(filters);
    }

    getFiltersFromStorage(key: string): IChoosenFilterInterface[] {
        return this._getValueFromSessionStorage<IChoosenFilterInterface[]>(key, []);
    }

    setFiltersList(key?: string, value?: IChoosenFilterInterface[], toEditSessionStorage: boolean = false): void {
        this._filtersList.next(value || []);

        if (toEditSessionStorage && key) {
            if (value === undefined) {
                sessionStorage.removeItem(key);
                return;
            }

            sessionStorage.setItem(key, JSON.stringify(value));
        }
    }

    setGrowFiltersList(key: string, value: IChoosenFilterInterface[], toEditSessionStorage: boolean = false): void {
        this._growFiltersList.next(value || []);

        if (toEditSessionStorage) {
            if (value === undefined) {
                sessionStorage.removeItem(key);
                return;
            }

            sessionStorage.setItem(key, JSON.stringify(value));
        }
    }

    selectAudience(value: MaxBrainAudience, isSingleSelect: boolean = false): void {
        this._selectedAudience.next(value);
        this.isSingleSelect = isSingleSelect;
    }

    findUsers(
        moduleIds: string[] = [],
        entityId = '',
        search = '',
        sortBy = '',
        sortOrder = '',
        pageSize = 10,
        pageIndex = 1,
        isUnassignedSelected = false,
        role = '',
        includeAnalytics = false,
        experienceIds: string[] = [],
        audienceIds: string[] = [],
        teamIds: string[] = [],
        extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [],
        isUnassignedSelectedExperiences = false,
        isUnassignedSelectedAudiences = false,
        isUnassignedSelectedTeams = false,
        columns: string[] = [],
        userRoleIds: string[] = [],
        userStatuses: string[] = [],
        statusIds: string[] = [],
        excludedTeamIds: string[] = [],
        teamRoleIds: string[] = []
    ): Observable<IUsersAndTotalCount> {
        let params: HttpParams = new HttpParams({
            encoder: new MyCustomHttpUrlEncodingCodec(),
        });

        params = params.set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (extendedAttributesFilters.length) {
            let attributeValues = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.textFilters?.length) {
                    const likeValues = attr.textFilters.filter((f) => f.mode === 'like').map((f) => f.value);
                    const eqValues = attr.textFilters.filter((f) => f.mode === 'eq').map((f) => f.value);

                    if (likeValues.length) {
                        params = params.append(`filter[${attr.attributeName}][like]`, likeValues.join(','));
                    }
                    if (eqValues.length) {
                        params = params.append(`filter[${attr.attributeName}][eq]`, eqValues.join(','));
                    }
                } else if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else {
                    params = params.set(`filter[${attr.attributeName}][gte]`, attr.rangeValue1);
                    params = params.set(`filter[${attr.attributeName}][lte]`, attr.rangeValue2);
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (entityId) {
            params = params.set('entityId', entityId);
        }

        if (includeAnalytics) {
            params = params.set('includeAnalytics', 'true');
        }

        if (search) {
            params = params.set('search', search);
        }

        if (moduleIds.length && !isUnassignedSelected) {
            params = params.append('filter[module]', moduleIds.map((moduleId) => moduleId).toString());
        }

        if (experienceIds.length && !isUnassignedSelectedExperiences) {
            params = params.append('filter[experience]', experienceIds.map((experienceId) => experienceId).toString());
        }

        if (audienceIds.length && !isUnassignedSelectedAudiences) {
            params = params.append('filter[audience]', audienceIds.map((audienceId) => audienceId).toString());
        }

        if (teamIds.length && !isUnassignedSelectedTeams) {
            params = params.append('filter[team]', teamIds.map((teamId) => teamId).toString());
        }

        if (excludedTeamIds.length) {
            params = params.append('filter[excludedTeam]', excludedTeamIds.map((teamId) => teamId).toString());
        }

        if (teamRoleIds.length) {
            params = params.append('filter[teamRole]', teamRoleIds.map((teamRoleId) => teamRoleId).toString());
        }

        if (userRoleIds.length) {
            params = params.append('filter[userRole]', userRoleIds.map((userRoleId) => userRoleId).toString());
        }

        if (userStatuses.length) {
            params = params.append('filter[status]', userStatuses.join(','));
        }

        if (statusIds.length) {
            params = params.append('filter[status]', statusIds.toString());
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        if (isUnassignedSelected) {
            params = params.set('filter[module]', 'null');
        }

        if (isUnassignedSelectedExperiences) {
            params = params.set('filter[experience]', 'null');
        }

        if (isUnassignedSelectedAudiences) {
            params = params.set('filter[audience]', 'null');
        }

        if (isUnassignedSelectedTeams) {
            params = params.set('filter[team]', 'null');
        }

        if (role) {
            params = params.set('role', role);
        }

        if (columns.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/users`, { params });
    }

    findCertificateOwners(search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1): Observable<IUsersAndTotalCount> {
        let params: HttpParams = new HttpParams();

        params = params.set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/certificates/owners`, { params });
    }

    findTeamMembersUsers(teamId = '', search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1, includeSubteams = false): Observable<IUsersAndTotalCount> {
        let params: HttpParams = new HttpParams();

        params = params.set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        if (includeSubteams) {
            params = params.set('includeSubteams', true.toString());
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/teams/${teamId}/team-members/users`, { params });
    }

    getAll(): Observable<User[]> {
        return this.http.get<IUserQuery[]>(`${this.environment.apiUrl}/users`).pipe(map((queries) => queries.map((query) => new User(query))));
    }
    getAvailableUsers(groupId = '', search = '', sortBy = '', sortOrder = '', pageSize = 5, pageIndex = 1): Observable<IUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (groupId) {
            params = params.set('groupId', groupId);
        }

        if (search) {
            params = params.set('search', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/users/participant`, { params });
    }

    getParticipantsAndResponsibles(moduleId: string, groupId = '', search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1): Observable<IUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (groupId) {
            params = params.set('groupId', groupId);
        }

        if (search) {
            params = params.set('search', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/modules/${moduleId}/participants-and-responsibles`, { params });
    }

    getGeneralGroupMembers(moduleId: string, search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1): Observable<IUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('search', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/modules/${moduleId}/general-group/members`, { params });
    }

    getRootAudienceUsers(audienceId: string, search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1): Observable<IRootAudienceUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('search', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IRootAudienceUsersAndTotalCount>(`${this.environment.apiUrl}/engage/audiences/${audienceId}/users`, { params });
    }

    async fetchResponsibleParticipantByEmail(moduleId: string, search: string): Promise<IUserQuery[]> {
        const params = new HttpParams().set('search', search);
        return this.http.get<IUserQuery[]>(`${this.environment.apiUrl}/modules/${moduleId}/participants-and-responsibles`, { params }).toPromise();
    }

    getCourseOfferingUsers(courseOfferingId = '', search = '', sortBy = '', sortOrder = '', pageSize = 10, pageIndex = 1): Observable<IUsersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex ? pageIndex.toString() : '1').set('limit', pageSize ? pageSize.toString() : '10');

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            if (sortBy === 'name' || sortBy === 'fullName') {
                sortBy = 'name';
            }

            params = params.set('sortBy', `${sortBy}`);
            params = params.set('sortOrder', sortOrder);
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/users`, {
            params,
        });
    }

    getCommunityUsers(
        entityId = '',
        search = '',
        sortBy = '',
        sortOrder = '',
        pageSize = 10,
        pageIndex = 1,
        extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [],
        columns: string[] = []
    ): Observable<IUsersAndTotalCount> {
        let params: HttpParams = new HttpParams({
            encoder: new MyCustomHttpUrlEncodingCodec(),
        });

        params = params.set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (extendedAttributesFilters.length) {
            let attributeValues = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.textFilters?.length) {
                    const likeValues = attr.textFilters.filter((f) => f.mode === 'like').map((f) => f.value);
                    const eqValues = attr.textFilters.filter((f) => f.mode === 'eq').map((f) => f.value);

                    if (likeValues.length) {
                        params = params.append(`filter[${attr.attributeName}][like]`, likeValues.join(','));
                    }
                    if (eqValues.length) {
                        params = params.append(`filter[${attr.attributeName}][eq]`, eqValues.join(','));
                    }
                } else if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else {
                    params = params.set(`filter[${attr.attributeName}][gte]`, attr.rangeValue1);
                    params = params.set(`filter[${attr.attributeName}][lte]`, attr.rangeValue2);
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            switch (sortBy) {
                case 'fullName':
                    sortBy = 'firstName';
                    break;
            }

            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        if (columns.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        return this.http.get<IUsersAndTotalCount>(`${this.environment.apiUrl}/engage/home-screen/community-widget/${entityId}/users`, { params });
    }
}
